// Базовые типы
export type MindboxStatus =
  | 'Success'
  | 'ValidationError'
  | 'ProtocolError'
  | 'InternalServerError';

export type MindboxBaseResponse = {
  status: MindboxStatus;
};

export type MindboxError = {
  status: Exclude<MindboxStatus, 'Success'>;
  message?: string;
};

export const MINDBOX_OPERATIONS = {
  AUTHORIZE_CUSTOMER: 'Website.AuthorizeCustomer',
  GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL: 'ProverkaNalichiyaPodpiski',
  GET_CUSTOMER_SUBSCRIPTIONS: 'Website.GetCustomerSubscriptions',
  EDIT_CUSTOMER_SUBSCRIPTIONS: 'Website.EditCustomerSubscriptions',
  GET_PRODUCT_SUBSCRIPTIONS: 'PodpiskaNaProduktNeVNalichii',
  VIEW_PRODUCT: 'Website.ViewProduct',
  VIEW_CATEGORY: 'Website.ViewCategory',
  CART_CREATE: 'Website.CartCreate',
  CART_CLEAR: 'Website.CartClear',
  FAVORITES_CREATE: 'Website.FavoritesCreate',
  FAVORITES_CLEAR: 'Website.FavoritesClear',
  CALCULATE_CATALOG_PRICES_AUTHORIZED:
    'Website.CalculateCatalogPricesAuthorized',
  CALCULATE_CATALOG_PRICES_ANONIM: 'Website.CalculateCatalogPricesAnonim',
  CATEGORY_POPULAR_PRODUCTS: 'Categorypopularproducts',
  PERSONAL_RECOMMENDATIONS: 'RealtimePersonalRecommendations',
  RELATED_PRODUCTS: 'Relatedproducts',
  POPULAR_PRODUCTS: 'PopularProducts',
  SIMILAR_PRODUCTS: 'Similarproducts',
  PRODUCT_RECOMMENDATIONS: 'Website.ProductRecommendations',
  PRODUCT_RECOMMENDATIONS_BY_CATEGORY:
    'Website.ProductRecommendationsByCategory',
  SET_PIGMENTATION_DIAGNOSTIKA_KOZHI_ITEM_LIST:
    'SetPigmentaciyaDiagnostikaKozhiItemList',
  SET_AKNE_DIAGNOSTIKA_KOZHI_ITEM_LIST: 'SetAkneDiagnostikaKozhiItemList',
  SET_ATOPICHESKIJ_DERMATIT_DIAGNOSTIKA_ITEM_LIST:
    'SetAtopicheskijDermatitDiagnostikaItemList',
  SET_CHUVSTVITELNAYA_KOZHA_DIAGNOSTIKA_ITEM_LIST:
    'SetCHuvstvitelnayaKozhaDiagnostikaItemList',
  SET_VOZRASTNYE_IZMENENIYA_DIAGNOSTIKA_ITEM_LIST:
    'SetVozrastnyeIzmeneniyaDiagnostikaItemList',
  SET_UNIVERSALNYJ_DIAGNOSTIKA_KOZHI_ITEM_LIST:
    'SetUniversalnyjDiagnostikaKozhiItemList',
  SET_SEBOREJNYJ_DERMATIT_DIAGNOSTIKA_ITEM_LIST:
    'SetSeborejnyjDermatitDiagnostikaItemList',
  SET_ROZACEA_DIAGNOSTIKA_KOZHI_ITEM_LIST: 'SetRozaceaDiagnostikaKozhiItemList',
  SET_SUXAYA_KOZHA_DIAGNOSTIKA_KOZHI_ITEM_LIST:
    'SetSuxayaKozhaDiagnostikaItemList',
  SET_OBEZVOZHYNNOST_DIAGNOSTIKA_KOZHI_ITEM_LIST:
    'SetObezvozhennostDiagnostikaKozhiItemList',
  GET_CUSTOMER_LOYALTY_LEVEL: 'Website.GetCustomerLoyaltyLevel',
  GET_CUSTOMER_INFO: 'Website.GetCustomerInfo',
  GET_CUSTOMER_BALANCE_HISTORY: 'Website.GetCustomerBalanceHistory',
  CREATE_REVIEW: 'OstavilOtzyvNaSajte',
  SUBSCRIBE_CUSTOMER: 'Website.SubscribeCustomer'
} as const;

// Тип операции на основе значений из MINDBOX_OPERATIONS
export type MindboxOperationType =
  (typeof MINDBOX_OPERATIONS)[keyof typeof MINDBOX_OPERATIONS];

// Базовые типы для общих полей
export type Subscription = {
  brand?: string;
  pointOfContact: string;
  topic?: string;
  isSubscribed: boolean;
};

export type CustomerBase = {
  ids: {
    bitrixClientId?: string;
    mindboxId?: number | string;
    referralCustomerCode?: string;
  };
};

type DiagnosticItemListBody = {
  customer?: {
    email?: string;
  };
  productList: ProductListCreate;
};

export type ProductListCreate = {
  count: number;
  pricePerItem?: number;
  priceOfLine?: number;
  customFields?: {
    acceptLevel?: string;
  } & Record<string, unknown>;
  product: {
    ids: {
      bitrixProducts: string;
    };
  };
  productGroup?: {
    ids: {
      bitrixProducts: string;
    };
  };
}[];

export type CustomerExtended = CustomerBase & {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  isEmailInvalid?: boolean;
  mobilePhone?: number;
  isMobilePhoneInvalid?: boolean;
  birthDate?: string;
  sex?: 'male' | 'female';
  changeDateTimeUtc?: string;
  ianaTimeZone?: string;
  timeZoneSource?: string;
  processingStatus?: string;
  area?: {
    name?: string;
    ids?: {
      externalId?: string;
    };
  };
  customFields?: Record<string, unknown>;
};

// Базовые типы для рекомендаций
export type RecommendationBase = {
  name: string;
  description?: string;
  displayName?: string;
  url?: string;
  pictureUrl?: string;
  price: number;
  oldPrice?: number;
  category?: string;
  vendorCode: string;
  ids: {
    mindboxId?: number;
    bitrixProducts?: string;
  };
  manufacturer?: {
    name: string;
  };
};

export type RecommendationResponse = MindboxBaseResponse & {
  recommendations: RecommendationBase[];
};

// Маппинг типов запросов
export type MindboxRequestBody = {
  [MINDBOX_OPERATIONS.AUTHORIZE_CUSTOMER]: {
    customer: CustomerBase;
  };
  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS]: {
    customer: CustomerBase;
  };

  [MINDBOX_OPERATIONS.GET_PRODUCT_SUBSCRIPTIONS]: {
    customer: {
      email: string;
      subscriptions?: Subscription[];
    };
    addProductToList: {
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.EDIT_CUSTOMER_SUBSCRIPTIONS]: {
    customer: CustomerBase & {
      email?: string;
      subscriptions?: Subscription[];
      customFields?: Record<string, unknown>;
    };
  };

  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL]: {
    customer: {
      email: string;
    };
  };

  [MINDBOX_OPERATIONS.VIEW_PRODUCT]: {
    viewProduct: {
      price?: number;
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
      customerAction?: {
        customFields?: Record<string, unknown>;
      };
    };
  };

  [MINDBOX_OPERATIONS.VIEW_CATEGORY]: {
    viewProductCategory: {
      productCategory: {
        ids: {
          bitrixProducts: string;
        };
      };
      customerAction?: {
        customFields?: Record<string, unknown>;
      };
    };
  };

  [MINDBOX_OPERATIONS.CART_CREATE]: {
    productList: ProductListCreate;
  };

  [MINDBOX_OPERATIONS.CART_CLEAR]: {
    customer?: CustomerBase;
  };

  [MINDBOX_OPERATIONS.FAVORITES_CREATE]: {
    customer?: CustomerBase;
    productList: ProductListCreate;
  };

  [MINDBOX_OPERATIONS.FAVORITES_CLEAR]: {
    customer: CustomerBase;
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED]: {
    customer: CustomerBase & {
      email?: string;
      mobilePhone?: string;
    };
    productList: {
      calculationDateTimeUtc?: string;
      area?: {
        ids: {
          externalId: string;
        };
      };
      items: Array<{
        basePricePerItem: string | number;
        minPricePerItem?: string | number;
        product: {
          ids: {
            bitrixProducts: string;
          };
        };
        requestedPromotions?: Array<{
          type: string;
          amount?: string;
          promotion?: {
            ids: {
              externalId: string;
            };
          };
          coupon?: {
            ids: {
              code: string;
            };
            pool?: {
              ids: {
                externalId: string;
              };
            };
          };
        }>;
      }>;
    };
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_ANONIM]: Omit<
    MindboxRequestBody[typeof MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED],
    'customer'
  > & {
    customer?: Omit<
      MindboxRequestBody[typeof MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED]['customer'],
      'ids'
    >;
  };

  [MINDBOX_OPERATIONS.CATEGORY_POPULAR_PRODUCTS]: {
    recommendation: {
      limit: number;
      area: {
        ids: {
          externalId: string;
        };
      };
      productCategory: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.PERSONAL_RECOMMENDATIONS]: {
    recommendation: {
      limit: number;
      area: {
        ids: {
          externalId: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.RELATED_PRODUCTS]: {
    recommendation: {
      limit: number;
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.POPULAR_PRODUCTS]: {
    recommendation: {
      limit: number;
      area: {
        ids: {
          externalId: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.SIMILAR_PRODUCTS]: {
    recommendation: {
      limit: number;
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS]: {
    recommendation: {
      limit: number;
      product: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };

  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS_BY_CATEGORY]: {
    recommendation: {
      limit: number;
      productCategory: {
        ids: {
          bitrixProducts: string;
        };
      };
    };
  };
  [MINDBOX_OPERATIONS.SET_PIGMENTATION_DIAGNOSTIKA_KOZHI_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_AKNE_DIAGNOSTIKA_KOZHI_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_ATOPICHESKIJ_DERMATIT_DIAGNOSTIKA_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_CHUVSTVITELNAYA_KOZHA_DIAGNOSTIKA_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_VOZRASTNYE_IZMENENIYA_DIAGNOSTIKA_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_UNIVERSALNYJ_DIAGNOSTIKA_KOZHI_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_SEBOREJNYJ_DERMATIT_DIAGNOSTIKA_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_ROZACEA_DIAGNOSTIKA_KOZHI_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_SUXAYA_KOZHA_DIAGNOSTIKA_KOZHI_ITEM_LIST]: DiagnosticItemListBody;
  [MINDBOX_OPERATIONS.SET_OBEZVOZHYNNOST_DIAGNOSTIKA_KOZHI_ITEM_LIST]: DiagnosticItemListBody;
};

// Маппинг типов ответов
export type MindboxResponseBody = {
  [MINDBOX_OPERATIONS.AUTHORIZE_CUSTOMER]: MindboxBaseResponse;

  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS]: MindboxBaseResponse & {
    customer: CustomerExtended & {
      subscriptions: Subscription[];
    };
  };

  [MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL]: MindboxBaseResponse & {
    customer: CustomerExtended & {
      subscriptions: Subscription[];
    };
  };

  [MINDBOX_OPERATIONS.GET_PRODUCT_SUBSCRIPTIONS]: MindboxBaseResponse;

  [MINDBOX_OPERATIONS.EDIT_CUSTOMER_SUBSCRIPTIONS]: MindboxBaseResponse & {
    customer: CustomerExtended & {
      subscriptions: Subscription[];
    };
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED]: MindboxBaseResponse & {
    customer?: {
      processingStatus: string;
    };
    productList: {
      processingStatus?: string;
      statusDescription?: string;
      items: Array<{
        basePricePerItem: number;
        minPricePerItem?: number;
        priceForCustomer?: number;
        priceWithDiscountsAndSpentBonusPoints?: number;
        product: {
          ids: {
            bitrixProducts: string;
          };
          promoLabels?: Array<{
            label: string;
            promotionExternalId: string;
          }>;
        };
        appliedPromotions?: Array<{
          type: string;
          groupingKey?: string;
          amount: number;
          promotion: {
            name?: string;
            type?: string;
            ids: {
              mindboxId?: number;
              externalId: string;
            };
          };
          balanceType?: { ids: { systemName: string } };
          coupon?: {
            ids: {
              code: string;
            };
            pool?: {
              name?: string;
              description?: string;
              ids: {
                mindboxId?: number;
                externalId?: string;
              };
            };
          };
          limits?: Array<{
            type: string;
            untilDateTimeUtc?: string;
            amount?: {
              type: string;
              value: number;
            };
            used?: {
              amount: number;
              usageServiceStatus: string;
            };
          }>;
        }>;
        earnBonusPoints?: Array<{
          amount: number;
          balance: {
            systemName: string;
          };
        }>;
      }>;
    };
  };

  [MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_ANONIM]: MindboxResponseBody[typeof MINDBOX_OPERATIONS.CALCULATE_CATALOG_PRICES_AUTHORIZED];

  [MINDBOX_OPERATIONS.CATEGORY_POPULAR_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.PERSONAL_RECOMMENDATIONS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.RELATED_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.POPULAR_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.SIMILAR_PRODUCTS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS]: RecommendationResponse;
  [MINDBOX_OPERATIONS.PRODUCT_RECOMMENDATIONS_BY_CATEGORY]: RecommendationResponse;
  [MINDBOX_OPERATIONS.SET_PIGMENTATION_DIAGNOSTIKA_KOZHI_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_AKNE_DIAGNOSTIKA_KOZHI_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_ATOPICHESKIJ_DERMATIT_DIAGNOSTIKA_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_CHUVSTVITELNAYA_KOZHA_DIAGNOSTIKA_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_VOZRASTNYE_IZMENENIYA_DIAGNOSTIKA_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_UNIVERSALNYJ_DIAGNOSTIKA_KOZHI_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_SEBOREJNYJ_DERMATIT_DIAGNOSTIKA_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_ROZACEA_DIAGNOSTIKA_KOZHI_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_SUXAYA_KOZHA_DIAGNOSTIKA_KOZHI_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.SET_OBEZVOZHYNNOST_DIAGNOSTIKA_KOZHI_ITEM_LIST]: MindboxBaseResponse;
  [MINDBOX_OPERATIONS.GET_CUSTOMER_LOYALTY_LEVEL]: MindboxBaseResponse & {
    balances: Array<{
      total: number;
      available: number;
      blocked: number;
      nearestExpiration: {
        total: number;
        date: string;
      };
      systemName: string;
      balanceType: {
        ids: {
          systemName: string;
        };
        name: string;
      };
    }>;
    customerSegmentations: Array<{
      segmentation: {
        ids: {
          externalId: string;
        };
        name: string;
      };
      segment: {
        ids: {
          externalId: string;
        };
        name: string;
      };
    }>;
  };
  [MINDBOX_OPERATIONS.GET_CUSTOMER_INFO]: MindboxBaseResponse & {
    balances: Array<{
      total: number;
      available: number;
      blocked: number;
      nearestExpiration: {
        total: number;
        date: string;
      };
      systemName: string;
      balanceType: {
        ids: {
          systemName: string;
        };
        name: string;
      };
    }>;
    customer: {
      customFields: {
        secondLevelEndDate?: string;
      };
    };
    retailOrderStatistics: {
      totalPaidAmount: number;
    };
  };
  [MINDBOX_OPERATIONS.GET_CUSTOMER_BALANCE_HISTORY]: MindboxBaseResponse & {
    customerActions: Array<{
      ids: {
        mindboxId: number;
      };
      actionTemplate: {
        systemName: string;
        name: string;
      };
      dateTimeUtc: string;
      pointOfContact: {
        ids: {
          externalId: string;
        };
      };
      customer: {
        ids: {
          mindboxId: number;
          bitrixClientId: string;
          referralCustomerCode: string;
        };
      };
      customerBalanceChanges: Array<{
        balanceType: {
          ids: {
            systemName: string;
          };
          name: string;
        };
        changeAmount: number;
        expirationDateTimeUtc: string;
        isAvailable: boolean;
        availableFromDateTimeUtc: string;
        balanceChangeKind: {
          systemName: string;
        };
      }>;
    }>;
  };
};

// Типы для использования в API
export type ResponseSuccess<T extends MindboxOperationType> =
  T extends keyof MindboxResponseBody
    ? MindboxResponseBody[T]
    : MindboxBaseResponse;

export type ResponseError = {
  error: MindboxError;
};

export type RequestBody<T extends MindboxOperationType> =
  T extends keyof MindboxRequestBody
    ? MindboxRequestBody[T]
    : Record<string, unknown>;
